import {
  describeAge,
  describeMonthsOfStock,
  describeSellThrough,
  explainSellThrough,
  MARKET_STALE_DAYS,
  PRICE_GUIDE_REGION_LABEL,
  type LotMarket,
  type MarketGuide,
  type SellThrough,
} from '@brickloop/core/schemas';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api } from '../api';
import { useMarketStatus } from '../market';
import { Button, Card, ErrorMessage, Notice, cn, money, number, plural, unitMoney, when } from '../ui';

const isStale = (m: LotMarket) => Date.now() - new Date(m.fetchedAt).getTime() > MARKET_STALE_DAYS * 86_400_000;

/** Why there is nothing to show for a lot, in plain words. */
function missingReason(m: LotMarket | null): string {
  if (!m) return 'No figures yet. They arrive in the overnight refresh, or open the lot and refresh it now.';
  if (m.status === 'pending') return 'Half the figures are in. The rest arrive in the next overnight refresh.';
  return 'BrickLink has no price guide for this lot.';
}

function sellThroughTone(s: SellThrough) {
  if (s.sellThrough === null) return 'text-slate-400';
  if (s.sellThrough >= 0.5) return 'font-medium text-emerald-700';
  if (s.sellThrough <= 0.2) return 'font-medium text-amber-700';
  return 'text-slate-800';
}

/** A short form of months of stock for a table cell. The full wording is in the tooltip. */
function shortMonths(s: SellThrough): string {
  if (s.state === 'no_recent_sales') return 'no recent sales';
  if (s.state === 'none_for_sale') return 'none for sale';
  if (s.monthsOfStock === null) return '—';
  const m = s.monthsOfStock;
  if (m < 1) return '<1';
  if (m < 10) return String(Math.round(m * 10) / 10);
  if (m < 24) return String(Math.round(m));
  return `${Math.round((m / 12) * 10) / 10} yrs`;
}

function tip(m: LotMarket, s: SellThrough) {
  return `${explainSellThrough(s)} ${PRICE_GUIDE_REGION_LABEL[m.region]}, all sellers on BrickLink. Fetched ${describeAge(m.fetchedAt)}${isStale(m) ? ' (out of date)' : ''}.`;
}

/** Sell-through in the inventory table. */
export function SellThroughCell({ market }: { market: LotMarket | null }) {
  const m = market;
  if (!m?.measure) {
    return (
      <span className="text-slate-400" title={missingReason(m)}>
        {m ? (m.status === 'pending' ? '…' : 'n/a') : '—'}
      </span>
    );
  }
  return (
    <span className={cn('tabular-nums', sellThroughTone(m.measure), isStale(m) && 'opacity-60')} title={tip(m, m.measure)}>
      {describeSellThrough(m.measure)}
    </span>
  );
}

/** Months of stock in the inventory table. */
export function MonthsCell({ market }: { market: LotMarket | null }) {
  const m = market;
  if (!m?.measure) {
    return (
      <span className="text-slate-400" title={missingReason(m)}>
        {m ? (m.status === 'pending' ? '…' : 'n/a') : '—'}
      </span>
    );
  }
  const long = m.measure.state === 'no_recent_sales' || m.measure.state === 'none_for_sale';
  return (
    <span className={cn('tabular-nums', long && 'text-xs text-slate-500', isStale(m) && 'opacity-60')} title={tip(m, m.measure)}>
      {shortMonths(m.measure)}
    </span>
  );
}

// ---------------------------------------------------------------- the lot page

function GuideColumn({ title, guide, empty }: { title: string; guide: MarketGuide | null; empty: string }) {
  return (
    <div>
      <h3 className="text-xs font-semibold tracking-wide text-slate-500 uppercase">{title}</h3>
      {guide ? (
        <>
          <p className="mt-1 text-2xl font-semibold tabular-nums">{number(guide.quantity)}</p>
          <p className="text-xs text-slate-500">{guide.lots === null ? 'pieces' : `pieces in ${plural(guide.lots, 'lot')}`}</p>
          {guide.quantity > 0 && (
            <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-sm">
              <dt className="text-slate-500">Average</dt>
              <dd className="tabular-nums">{unitMoney(guide.avg, guide.currency ?? 'GBP')}</dd>
              <dt className="text-slate-500">Average, by quantity</dt>
              <dd className="tabular-nums">{unitMoney(guide.qtyAvg, guide.currency ?? 'GBP')}</dd>
              <dt className="text-slate-500">Lowest</dt>
              <dd className="tabular-nums">{unitMoney(guide.min, guide.currency ?? 'GBP')}</dd>
              <dt className="text-slate-500">Highest</dt>
              <dd className="tabular-nums">{unitMoney(guide.max, guide.currency ?? 'GBP')}</dd>
            </dl>
          )}
        </>
      ) : (
        <p className="mt-1 text-sm text-slate-400">{empty}</p>
      )}
    </div>
  );
}

/**
 * The market card on a lot page: what sold and what is for sale across all of BrickLink, how fast it moves, and
 * how old the numbers are. Reading costs nothing; the button asks BrickLink for this one lot (two requests).
 */
export function MarketCard({ lotId, market, writable }: { lotId: number; market: LotMarket | null; writable: boolean }) {
  const qc = useQueryClient();
  const refresh = useMutation({
    mutationFn: () => api<{ requests: number }>(`/api/v1/lots/${lotId}/market/refresh`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lot', String(lotId)] });
      void qc.invalidateQueries({ queryKey: ['lots'] });
      void qc.invalidateQueries({ queryKey: ['market-status'] });
    },
  });

  const m = market;
  const s = m?.measure ?? null;

  return (
    <Card
      title="BrickLink market (all sellers)"
      className="lg:col-span-3"
      actions={
        writable && (
          <Button variant="secondary" small disabled={refresh.isPending} onClick={() => refresh.mutate()}>
            {refresh.isPending ? 'Asking BrickLink…' : 'Refresh from BrickLink'}
          </Button>
        )
      }
    >
      <div className="space-y-3">
        {s && m ? (
          <>
            <div className="flex flex-wrap items-end gap-x-8 gap-y-2">
              <div>
                <div className="text-xs font-semibold tracking-wide text-slate-500 uppercase">Sell-through</div>
                <div className={cn('text-3xl font-semibold tabular-nums', sellThroughTone(s))}>{describeSellThrough(s)}</div>
              </div>
              <div>
                <div className="text-xs font-semibold tracking-wide text-slate-500 uppercase">Months of stock</div>
                <div className="text-3xl font-semibold">{describeMonthsOfStock(s)}</div>
              </div>
              <p className="max-w-xl text-sm text-slate-600">{explainSellThrough(s)}</p>
            </div>
            <div className="grid gap-6 border-t border-slate-100 pt-3 sm:grid-cols-2">
              <GuideColumn title="Sold in the last 6 months" guide={m.sold} empty="" />
              <GuideColumn title="For sale now" guide={m.forSale} empty="" />
            </div>
          </>
        ) : (
          <p className="text-sm text-slate-600">{missingReason(m)}</p>
        )}

        <p className={cn('text-xs', m && isStale(m) ? 'font-medium text-amber-700' : 'text-slate-500')}>
          {m
            ? `${PRICE_GUIDE_REGION_LABEL[m.region]}. Fetched ${describeAge(m.fetchedAt)} (${when(m.fetchedAt)})${isStale(m) ? '. Out of date: the overnight refresh will get to it.' : '.'}`
            : 'Nothing fetched for this lot yet.'}{' '}
          Prices are in {m?.sold?.currency ?? m?.forSale?.currency ?? 'the shop currency'}. These are everyone's sales and listings, not only yours.
        </p>
        {m?.status === 'unavailable' && m.note && <p className="text-xs text-slate-500">BrickLink said: {m.note}</p>}

        {refresh.isSuccess && <Notice tone="green">Fetched from BrickLink just now ({plural(refresh.data.requests, 'request')} from today&apos;s allowance).</Notice>}
        <ErrorMessage error={refresh.error} />
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------- freshness, in words

interface RunDetail {
  skipped?: string;
  requests?: number;
  refreshed?: number;
  unavailable?: number;
  stoppedBecause?: string;
  message?: string;
}

const STOPPED: Record<string, string> = {
  finished: 'finished everything that was due',
  nothing_due: 'found nothing due',
  run_limit: 'stopped at its limit for one night',
  budget: "stopped to keep today's request reserve",
  refused: 'was stopped because BrickLink refused a request',
  bad_response: 'was stopped because BrickLink answered with something unexpected',
  unreachable: 'could not reach BrickLink',
};

export function describeLastRun(run: { at: string; ok: boolean; detail: unknown } | null): string {
  if (!run) return 'The overnight refresh has not run yet.';
  const d = (run.detail ?? {}) as RunDetail;
  const when_ = `Last overnight refresh: ${describeAge(run.at)}`;
  if (d.skipped) return `${when_}. It did nothing: ${d.skipped}.`;
  const did = d.requests !== undefined ? `, made ${plural(d.requests, 'request')} and updated ${plural(d.refreshed ?? 0, 'lot')}` : '';
  return `${when_}${did}; it ${STOPPED[d.stoppedBecause ?? ''] ?? 'ended'}${d.message && !run.ok ? ` (${d.message})` : ''}.`;
}

/** One plain sentence, shown over the inventory table: how many lots have figures, and how old they are. */
export function MarketFreshness({ connected }: { connected?: boolean }) {
  const status = useMarketStatus();
  const s = status.data;
  if (!s) return null;
  if (s.lotsInStock === 0) return null;

  const region = PRICE_GUIDE_REGION_LABEL[s.region];
  const head = `Sell-through and months of stock come from BrickLink's price guide: every seller's sales over the last 6 months and every seller's listings now (${region}), not just yours.`;
  if (s.withFigures === 0) {
    return (
      <Notice tone="blue">
        {head} No lot has figures yet.{' '}
        {connected === false ? (
          <>
            Turn on the BrickLink connection in <Link className="underline" to="/channels">Channels</Link> and they fill in overnight.
          </>
        ) : (
          'They fill in overnight, or open a lot and press "Refresh from BrickLink".'
        )}
      </Notice>
    );
  }
  return (
    <Notice tone={s.stale > 0 ? 'amber' : 'blue'}>
      {head} {number(s.withFigures)} of {number(s.lotsInStock)} lots in stock have figures
      {s.oldestFetchedAt && s.newestFetchedAt && describeAge(s.oldestFetchedAt) === describeAge(s.newestFetchedAt) ? (
        <>; all fetched {describeAge(s.oldestFetchedAt)}</>
      ) : (
        <>
          {s.oldestFetchedAt && <>; the oldest were fetched {describeAge(s.oldestFetchedAt)}</>}
          {s.newestFetchedAt && <>, the newest {describeAge(s.newestFetchedAt)}</>}
        </>
      )}
      .{' '}
      {s.nightsToCatchUp > 1 ? `The overnight refresh needs about ${s.nightsToCatchUp} nights to get through what is due.` : s.due > 0 ? 'The rest arrive in tonight’s refresh.' : 'Everything is up to date.'}
    </Notice>
  );
}
